import React from "react";
import { Coins, X as XIcon, Compass, Sparkles } from "lucide-react";

import { MITEL_BANKNOTES } from "./mitel/mitelData";
import MitelBanknoteCard from "./mitel/MitelBanknoteCard";
import MitelAccordion from "./mitel/MitelAccordion";

/**
 * MitelGuide — La pagina/esperienza MITEL nel Centro Guida.
 *
 * Fonte ufficiale dei testi: documento
 * "Assegnazione ufficiale delle banconote MITEL".
 *
 * Filosofia:
 *  - MITEL non è finanza: nessun €, $, grafici, ticker.
 *  - MITEL è un atlante interattivo del Tempo attraverso le banconote.
 *  - Additivo e non distruttivo: nessuna interazione con Portafoglio,
 *    TIME Watch, Area Personale, NDA, Iubenda, Stripe.
 *  - Design coerente con `TimeWatchGuide` / `GuideCenter`
 *    (glass-card emerald, tipografia "Apple-like", parola "Tempo"
 *    sempre capitalizzata).
 *  - Data-driven: le banconote provengono da `mitelData.js`.
 */

function SectionTitle({ eyebrow, title }) {
  return (
    <div className="mb-3">
      {eyebrow && (
        <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-1.5">
          {eyebrow}
        </div>
      )}
      <h3 className="text-lg sm:text-xl font-light text-white leading-snug tracking-tight">
        {title}
      </h3>
    </div>
  );
}

export default function MitelGuide({ open, onClose, onBack }) {
  if (!open) return null;

  const availableBanknotes = MITEL_BANKNOTES.filter((b) => b.available);

  return (
    <div
      data-testid="mitel-guide"
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mitel-guide-title"
    >
      <div
        className="glass-card relative w-full max-w-2xl p-5 sm:p-8 border-emerald-400/25 shadow-[0_25px_80px_-20px_rgba(16,185,129,0.35)] max-h-[92vh] overflow-y-auto overflow-x-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="mitel-guide-close"
          aria-label="Chiudi Guida MITEL"
          className="absolute top-4 right-4 p-1 rounded-full text-white/50 hover:text-white hover:bg-white/5 transition"
        >
          <XIcon className="w-4 h-4" />
        </button>

        {/* === HERO === */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center text-emerald-300">
            <Coins className="w-4 h-4" />
          </div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300">
            MITEL
          </div>
        </div>
        <h2
          id="mitel-guide-title"
          data-testid="mitel-guide-title"
          className="text-3xl sm:text-4xl font-light text-white tracking-tight leading-tight mb-2"
        >
          MITEL
        </h2>
        <p className="text-sm sm:text-base font-light text-white/70 leading-relaxed">
          Minuti di Tempo Libero
        </p>
        <p className="mt-1 text-[11px] tracking-[0.28em] uppercase text-emerald-300/85 mb-4">
          MI = Minuti · TE = Tempo · L = Libero
        </p>

        {/* === DEFINIZIONE NON NEGOZIABILE === */}
        <div
          data-testid="mitel-definition"
          className="rounded-2xl border border-emerald-400/20 bg-emerald-400/[0.04] px-4 sm:px-5 py-4 sm:py-5"
        >
          <p className="text-[13px] sm:text-sm font-light text-white/90 leading-relaxed">
            <span className="text-white">MITEL</span> significa Minuti di Tempo
            Libero. Nasce per rendere visibile, comprensibile e tangibile
            qualcosa che normalmente possiamo soltanto vedere passare: il
            nostro <span className="text-white">Tempo</span> libero.
          </p>
          <p className="mt-3 text-[13px] sm:text-sm font-light text-white/85 leading-relaxed">
            <span className="text-emerald-300/95">1 MITEL = 1 minuto</span> di
            Tempo libero.
          </p>
          <p className="mt-3 text-[12px] sm:text-[13px] font-light text-white/70 leading-relaxed">
            Nel sistema TIME viene inoltre utilizzato il principio
            convenzionale:{" "}
            <span className="text-emerald-300/90">1 € = 1 minuto</span>.
          </p>
          <p className="mt-2 text-[12px] sm:text-[13px] font-light text-white/60 leading-relaxed">
            Non significa attribuire un prezzo alla vita. Non significa che il
            Tempo possa essere realmente comprato, venduto o posseduto. È una
            regola di rappresentazione attraverso cui TIME cambia
            prospettiva:{" "}
            <span className="text-white/85">dal denaro al Tempo</span>.
          </p>
        </div>

        {/* === COSA NON È — nota elegante === */}
        <section
          data-testid="mitel-not"
          className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] px-4 sm:px-5 py-4"
        >
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-2">
            Chiarimento
          </div>
          <p className="text-[13px] sm:text-sm font-light text-white/80 leading-relaxed">
            MITEL non è:
          </p>
          <ul className="mt-2 space-y-1 text-[12px] sm:text-[13px] font-light text-white/60 leading-relaxed list-disc list-inside">
            <li>una valuta legale;</li>
            <li>una criptovaluta;</li>
            <li>un mezzo di pagamento tradizionale;</li>
            <li>una promessa finanziaria.</li>
          </ul>
          <p className="mt-3 text-[13px] sm:text-sm font-light text-white/85 leading-relaxed">
            MITEL è prima di tutto un{" "}
            <span className="text-white">linguaggio</span> e una
            rappresentazione simbolica del Tempo libero.
          </p>
          <p className="mt-2 text-[12px] sm:text-[13px] font-light text-white/60 leading-relaxed">
            Le banconote MITEL sono opere simboliche e collezionabili. Il loro
            eventuale valore economico non rappresenta né determina il valore
            del Tempo umano.
          </p>
        </section>

        {/* Divider */}
        <div className="h-px bg-white/8 my-7" />

        {/* === ATLANTE BANCONOTE === */}
        <section data-testid="mitel-banknotes" className="mb-2">
          <SectionTitle
            eyebrow="Atlante MITEL"
            title="Le 11 banconote MITEL"
          />
          <p className="text-[13px] sm:text-sm font-light text-white/60 leading-relaxed mb-2">
            Ogni banconota rappresenta Tempo.
          </p>
          <p className="text-[13px] sm:text-sm font-light text-white/60 leading-relaxed mb-2">
            Ogni volto mostra un modo di usarlo.
          </p>
          <p className="text-[13px] sm:text-sm font-light text-white/60 leading-relaxed mb-6">
            Ogni opera mostra ciò che può restare.
          </p>

          <ol
            data-testid="mitel-banknotes-list"
            className="space-y-10 sm:space-y-12 list-none pl-0"
          >
            {availableBanknotes.map((b) => (
              <React.Fragment key={b.id}>
                {b.tier === "vissuto" && (
                  <li
                    data-testid="mitel-tier-vissuto"
                    className="!mt-14 sm:!mt-16"
                  >
                    <div className="flex items-center gap-4 max-w-xl mx-auto">
                      <span className="h-px flex-1 bg-emerald-400/25" />
                      <span className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/85 whitespace-nowrap">
                        Tempo vissuto
                      </span>
                      <span className="h-px flex-1 bg-emerald-400/25" />
                    </div>
                    <p className="mt-3 text-center text-[12px] sm:text-[13px] font-light text-white/60 leading-relaxed max-w-lg mx-auto">
                      Il Tempo non viene più percepito come singolo momento,
                      ma come una parte significativa dell'esistenza.
                    </p>
                  </li>
                )}
                {b.tier === "narrativo" && (
                  <li
                    data-testid="mitel-tier-narrativo"
                    className="!mt-14 sm:!mt-16"
                  >
                    <div className="flex items-center gap-4 max-w-xl mx-auto">
                      <span className="h-px flex-1 bg-amber-400/30" />
                      <span className="text-[10px] tracking-[0.32em] uppercase text-amber-300/90 whitespace-nowrap">
                        Tempo narrativo
                      </span>
                      <span className="h-px flex-1 bg-amber-400/30" />
                    </div>
                    <p className="mt-3 text-center text-[12px] sm:text-[13px] font-light text-white/65 leading-relaxed max-w-lg mx-auto">
                      Un anno non si spende. Si attraversa.
                    </p>
                  </li>
                )}
                <li>
                  <MitelBanknoteCard
                    banknote={b}
                    testId={`mitel-banknote-${b.id}`}
                  />
                </li>
              </React.Fragment>
            ))}
          </ol>
        </section>

        {/* Divider */}
        <div className="h-px bg-white/8 my-7" />

        {/* === LA FILOSOFIA MITEL === */}
        <section data-testid="mitel-deep" className="space-y-3">
          <SectionTitle eyebrow="La filosofia MITEL" title="Per andare più a fondo" />

          {/* 1 — COS'È MITEL */}
          <MitelAccordion
            eyebrow="1 — Definizione"
            title="Cos'è MITEL"
            testId="mitel-deep-what"
          >
            <p>
              MITEL è l'unità con cui il Progetto TIME rende visibile e
              tangibile il Tempo libero.
            </p>
            <p>
              Non misura il valore di una persona. Non stabilisce un prezzo
              per un'ora di vita. Non pretende di trasformare il Tempo in una
              proprietà.
            </p>
            <p>
              Crea invece un linguaggio attraverso il quale qualcosa di
              astratto può diventare visibile, misurabile e comprensibile.
            </p>
            <p className="text-white/85">
              La sua funzione non è dare un prezzo al Tempo. È farci
              accorgere che esiste.
            </p>
          </MitelAccordion>

          {/* 2 — COSA NON È MITEL */}
          <MitelAccordion
            eyebrow="2 — Confini"
            title="Cosa non è MITEL"
            testId="mitel-deep-not"
          >
            <p>MITEL non è:</p>
            <ul className="list-disc list-inside space-y-1 text-white/70">
              <li>una valuta legale;</li>
              <li>una criptovaluta;</li>
              <li>un mezzo di pagamento tradizionale;</li>
              <li>una promessa finanziaria.</li>
            </ul>
            <p className="text-white/85">
              È prima di tutto un linguaggio e una rappresentazione simbolica
              del Tempo libero.
            </p>
            <p>
              Le banconote MITEL sono opere simboliche e collezionabili. Il
              loro eventuale valore economico non rappresenta né determina il
              valore del Tempo umano.
            </p>
          </MitelAccordion>

          {/* 3 — PERCHÉ ESISTE MITEL */}
          <MitelAccordion
            eyebrow="3 — Origine"
            title="Perché esiste MITEL"
            testId="mitel-deep-why"
          >
            <p>
              Il mondo è abituato a trasformare il Tempo in denaro. Si lavora
              per ore. Le ore producono denaro. Il denaro viene contato. Il
              Tempo impiegato per ottenerlo, invece, spesso scompare dalla
              nostra percezione.
            </p>
            <p className="text-white/85">
              MITEL compie l'operazione opposta: riporta il denaro al Tempo.
            </p>
            <p>
              Una disponibilità economica può così essere osservata anche
              attraverso un'altra domanda: “Quanto Tempo libero
              rappresenta?”. Non perché quel Tempo sia garantito,
              acquistabile o realmente posseduto, ma perché cambiare unità di
              misura cambia il modo in cui osserviamo ciò che abbiamo.
            </p>
            <p>MITEL nasce da una consapevolezza semplice:</p>
            <ul className="list-disc list-inside space-y-1 text-white/70">
              <li>il denaro può richiedere Tempo per essere ottenuto;</li>
              <li>il Tempo trascorso non torna;</li>
              <li>
                una maggiore libertà economica può offrire maggiori
                possibilità di scegliere come impiegare il proprio Tempo;
              </li>
              <li>
                imparare a riconoscere il Tempo significa imparare a
                scegliere come viverlo.
              </li>
            </ul>
            <p className="text-white/85">
              MITEL non dice quanto vale una persona. Rende visibile una
              possibilità di Tempo libero.
            </p>
          </MitelAccordion>

          {/* 4 — DAL DENARO AL TEMPO */}
          <MitelAccordion
            eyebrow="4 — Prospettiva"
            title="Dal denaro al Tempo"
            testId="mitel-deep-money"
          >
            <p>
              La conversione utilizzata da TIME non attribuisce un prezzo
              alla vita. È una regola di rappresentazione.
            </p>
            <p>
              Serve a trasformare un numero economico in qualcosa che
              l'essere umano possa percepire immediatamente:{" "}
              <span className="text-white/85">
                denaro → Tempo → possibilità di scelta
              </span>
              .
            </p>
            <p>
              Un saldo smette così, per un momento, di essere soltanto una
              cifra. Può essere osservato come minuti, ore, giorni,
              settimane, mesi o anni.
            </p>
            <p className="text-white/85">
              Ed è qui che MITEL cambia la domanda. Non soltanto: “Quanto
              possiedo?” ma: “Quanto Tempo potrebbe rappresentare?”
            </p>
          </MitelAccordion>

          {/* 5 — PERCHÉ VOLTI E OPERE */}
          <MitelAccordion
            eyebrow="5 — Narrativa"
            title="Perché le banconote hanno volti e opere"
            testId="mitel-deep-faces"
          >
            <p>
              Le banconote MITEL non utilizzano personaggi e monumenti come
              semplici decorazioni. Ogni banconota racconta una relazione:
            </p>
            <p className="text-white/85">
              Tempo → scelta → azione → traccia.
            </p>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-2">
                I volti
              </div>
              <p>
                I personaggi non vengono presentati come eroi perfetti.
                Rappresentano differenti modi attraverso cui esseri umani
                hanno inciso sul proprio Tempo e su quello venuto dopo:
              </p>
              <ul className="mt-2 space-y-1 text-white/70">
                <li>Huygens → misurare</li>
                <li>Marx → cambiare</li>
                <li>Michelangelo → creare</li>
                <li>Machiavelli → conquistare</li>
                <li>Colombo → scoprire</li>
                <li>Cesare → costruire</li>
                <li>Augusto → attraversare</li>
                <li>Galileo → osservare</li>
                <li>San Francesco → vivere</li>
                <li>Leonardo → inventare</li>
                <li>Dante → restare</li>
              </ul>
              <p className="mt-3 text-white/70">
                La loro presenza non implica approvazione di ogni azione,
                pensiero o conseguenza storica associata alla loro vita. Nel
                sistema MITEL assumono un significato simbolico e narrativo.
              </p>
            </div>
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-2">
                Le opere
              </div>
              <p>Sull'altro lato del racconto troviamo:</p>
              <ul className="mt-2 space-y-1 text-white/70">
                <li>Piramidi di Giza;</li>
                <li>Colosseo;</li>
                <li>Grande Muraglia Cinese;</li>
                <li>Taj Mahal;</li>
                <li>Machu Picchu;</li>
                <li>Basilica di San Pietro;</li>
                <li>Notre-Dame de Paris;</li>
                <li>Angkor Wat;</li>
                <li>Stonehenge;</li>
                <li>Torre Eiffel;</li>
                <li>Statua della Libertà.</li>
              </ul>
              <p className="mt-3 text-white/70">
                Appartengono a epoche, civiltà e culture differenti. Non
                devono necessariamente avere un rapporto storico diretto con
                il personaggio raffigurato sulla stessa banconota.{" "}
                <span className="text-white/85">Il collegamento è simbolico.</span>
              </p>
              <p className="mt-2 text-white/85">
                Il volto rappresenta chi utilizza il proprio Tempo. L'opera
                rappresenta ciò che può restare dopo quel Tempo.
              </p>
              <p className="mt-2 text-white/90 italic">
                I volti mostrano come il Tempo può essere usato. Le opere
                mostrano cosa il Tempo può lasciare.
              </p>
              <p className="mt-3 text-emerald-300/90">
                Ogni banconota educa, non paga.
              </p>
            </div>
          </MitelAccordion>

          {/* 6 — PERCHÉ ESISTONO TAGLI TEMPORALI */}
          <MitelAccordion
            eyebrow="6 — Progressione"
            title="Perché esistono tagli temporali"
            testId="mitel-deep-cuts"
          >
            <p>MITEL cambia progressivamente scala.</p>
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-1">
                Da 5 a 60 MITEL — Tempo operativo
              </div>
              <p className="text-white/70">
                Piccole porzioni di Tempo libero. Un pensiero. Una scelta.
                Una conversazione. Un'azione. Un inizio. Servono a ricordare
                che anche pochi minuti sono Tempo reale.
              </p>
            </div>
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-1">
                1.440 MITEL — 1 DAY
              </div>
              <p className="text-white/70">
                Un giorno di Tempo libero. La prima grande unità completa
                della vita quotidiana.
              </p>
            </div>
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-1">
                10.080 MITEL — 1 WEEK
              </div>
              <p className="text-white/70">
                Una settimana di Tempo libero. Il Tempo non viene più
                percepito come singolo momento, ma come una parte
                significativa dell'esistenza.
              </p>
            </div>
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-1">
                43.800 MITEL — 1 MONTH
              </div>
              <p className="text-white/70">
                Un mese medio di Tempo libero. Il mese MITEL rappresenta un
                dodicesimo dell'anno. Per questo il suo valore è 43.800, e
                non quello di un mese convenzionale fisso di 30 giorni.
              </p>
            </div>
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-1">
                525.600 MITEL — 1 YEAR
              </div>
              <p className="text-white/70">
                Un anno di Tempo libero. A questa scala MITEL smette quasi
                di sembrare una banconota e diventa la rappresentazione di
                una porzione enorme dell'esistenza.
              </p>
              <p className="mt-2 text-white/90 italic">
                Un anno non si spende. Si attraversa.
              </p>
            </div>
          </MitelAccordion>

          {/* 7 — COSA FA MITEL NEL PROGETTO TIME */}
          <MitelAccordion
            eyebrow="7 — Ecosistema"
            title="Cosa fa MITEL nel progetto TIME"
            testId="mitel-deep-ecosystem"
          >
            <p>MITEL serve a:</p>
            <ul className="list-disc list-inside space-y-1 text-white/70">
              <li>rendere visibile il Tempo libero;</li>
              <li>
                trasformare un concetto astratto in qualcosa di comprensibile;
              </li>
              <li>
                mostrare il rapporto tra disponibilità economica e possibilità
                di liberare Tempo;
              </li>
              <li>educare alla scelta del Tempo;</li>
              <li>
                ricordare che dietro un saldo può esistere anche una
                possibilità di vita;
              </li>
              <li>
                spostare l'attenzione dal semplice possesso a ciò che
                scegliamo di fare del nostro Tempo.
              </li>
            </ul>
            <p className="text-white/85">
              MITEL è la <span className="text-white">grammatica</span> del
              PROGETTO TIME. TIME Watch è lo{" "}
              <span className="text-white">strumento</span> che rende il
              Tempo visibile e misurabile. Il libro TIME ne costruisce la{" "}
              <span className="text-white">coscienza</span>.
            </p>
          </MitelAccordion>

          {/* 8 — LA RAPPRESENTAZIONE TANGIBILE DEL TEMPO LIBERO */}
          <MitelAccordion
            eyebrow="8 — Tangibilità"
            title="La rappresentazione tangibile del Tempo libero"
            testId="mitel-deep-tangible"
          >
            <p>
              Le banconote MITEL rappresentano la materializzazione simbolica
              del concetto. Sono la rappresentazione grafica, cartacea e
              tangibile del Tempo libero.
            </p>
            <p>
              Permettono di osservare una disponibilità economica da una
              prospettiva differente. Non soltanto: “Quanto denaro possiedo?”
              ma: “Quanto Tempo libero rappresenta?”
            </p>
            <p>
              Una banconota MITEL rende fisicamente percepibile questa
              domanda. Permette simbolicamente di tenere tra le mani qualcosa
              che normalmente non possiamo afferrare: il Tempo.
            </p>
            <p className="text-white/85">
              MITEL non afferma che quel Tempo sia realmente posseduto o
              garantito. Lo rappresenta. Ed è attraverso questa
              rappresentazione che cerca di modificarne la percezione.
            </p>
          </MitelAccordion>

          {/* 9 — IL PRINCIPIO DELLE BANCONOTE */}
          <MitelAccordion
            eyebrow="9 — Principio"
            title="Il principio delle banconote"
            testId="mitel-deep-principle"
          >
            <p>
              MITEL non nasce per rappresentare semplicemente un valore.
              Nasce per rappresentare Tempo libero.
            </p>
            <p>
              I volti mostrano come il Tempo può essere usato. Le opere
              mostrano cosa può restare dopo che quel Tempo è passato.
            </p>
            <p>Ogni banconota diventa così una piccola mappa:</p>
            <p className="text-white/85">
              quanto Tempo hai → come scegli di usarlo → cosa decidi di
              lasciarne.
            </p>
            <p className="text-white/85">
              La narrativa è la costituzione. Le banconote sono mappe di
              coscienza.
            </p>
          </MitelAccordion>

          {/* 10 — DIMENSIONI FISICHE UFFICIALI */}
          <MitelAccordion
            eyebrow="10 — Dimensioni ufficiali"
            title="Le dimensioni fisiche delle 11 banconote"
            testId="mitel-deep-sizes"
          >
            <p>
              Le dimensioni FISICHE ufficiali MITEL sono progressive e
              differenti dalle dimensioni con cui le banconote vengono
              visualizzate qui nel Centro Guida (uniformate al riferimento
              della 5 MITEL per motivi di leggibilità).
            </p>
            <ul className="mt-2 space-y-1 text-white/70">
              <li>5 MITEL — 120 × 62 mm</li>
              <li>10 MITEL — 122 × 64 mm</li>
              <li>20 MITEL — 124 × 66 mm</li>
              <li>30 MITEL — 126 × 68 mm</li>
              <li>40 MITEL — 128 × 70 mm</li>
              <li>50 MITEL — 130 × 72 mm</li>
              <li>60 MITEL — 132 × 74 mm</li>
              <li>1.440 MITEL — 1 DAY — 134 × 76 mm</li>
              <li>10.080 MITEL — 1 WEEK — 134 × 78 mm</li>
              <li>43.800 MITEL — 1 MONTH — 134 × 80 mm</li>
              <li>525.600 MITEL — 1 YEAR — 134 × 82 mm</li>
            </ul>
          </MitelAccordion>
        </section>

        {/* === PRINCIPIO NARRATIVO === */}
        <section
          data-testid="mitel-principle"
          className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] px-4 sm:px-5 py-5"
        >
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-emerald-300/80" aria-hidden="true" />
            <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80">
              Principio
            </div>
          </div>
          <p className="text-[13px] sm:text-sm font-light text-white/85 leading-relaxed">
            MITEL non rappresenta valore. Rappresenta{" "}
            <span className="text-white">scelte di Tempo</span>.
          </p>
          <p className="mt-2 text-[12px] sm:text-[13px] font-light text-white/65 leading-relaxed">
            I volti mostrano come usarlo. Le opere dimostrano cosa resta.
          </p>
          <p className="mt-2 text-[12px] sm:text-[13px] font-light text-white/65 leading-relaxed">
            La narrativa è la costituzione. Le banconote sono mappe di
            coscienza.
          </p>
        </section>

        {/* === MANIFESTO FINALE === */}
        <section
          data-testid="mitel-manifesto"
          className="mt-10 mb-4 text-center px-2"
        >
          <div className="mx-auto max-w-lg">
            <p className="text-2xl sm:text-3xl font-light text-white tracking-tight leading-tight">
              Il Tempo non si risparmia.
            </p>
            <p className="mt-2 text-2xl sm:text-3xl font-light text-white tracking-tight leading-tight">
              Si <span className="italic text-emerald-300/95">riconosce</span>.
            </p>
            <p className="mt-6 text-[11px] tracking-[0.32em] uppercase text-emerald-300/80">
              MITEL è il suo nome.
            </p>
          </div>
        </section>

        {/* === ACTIONS === */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/8">
          <button
            type="button"
            onClick={onBack || onClose}
            data-testid="mitel-guide-back"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-[11px] tracking-[0.22em] uppercase text-white/60 hover:text-white hover:border-white/30 transition"
          >
            <Compass className="w-3.5 h-3.5" />
            Torna al Centro Guida
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="mitel-guide-ok"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition"
          >
            HO CAPITO
          </button>
        </div>
      </div>
    </div>
  );
}
